import { useQuery } from '@tanstack/react-query'
import api from 'api/api'
import { SATOSHIS } from 'lib/bsv-util'

const BSV_EXCHANGE_RATE_CACHE_KEY = 'treechat:bsv-usd-exchange-rate'
const BSV_EXCHANGE_RATE_CACHE_TTL_MS = 60 * 60 * 1000

type BsvExchangeRateResult = {
    exchangerate?: number | null
    unavailable?: boolean
}

type CachedBsvExchangeRate = {
    exchangerate: number
    cachedAt: number
}

type BsvExchangeRateOptions = {
    enabled?: boolean
}

export function satsToUsd(sats: number | null | undefined, exchangeRate: number | null) {
    if (sats === 0) return 0
    if (sats == null || !exchangeRate) return null

    return (sats / SATOSHIS) * exchangeRate
}

export function usdToSats(dollars: number | null | undefined, exchangeRate: number | null) {
    if (dollars === 0) return 0
    if (!dollars || !exchangeRate) return null

    return Math.max(1, Math.round((dollars / exchangeRate) * SATOSHIS))
}

function readCachedBsvExchangeRate({
    allowStale = false,
}: { allowStale?: boolean } = {}): CachedBsvExchangeRate | null {
    if (typeof window === 'undefined') return null

    try {
        const cachedRaw = window.localStorage.getItem(BSV_EXCHANGE_RATE_CACHE_KEY)
        if (!cachedRaw) return null

        const cached = JSON.parse(cachedRaw) as Partial<CachedBsvExchangeRate>
        if (
            typeof cached.exchangerate !== 'number' ||
            !Number.isFinite(cached.exchangerate) ||
            cached.exchangerate <= 0 ||
            typeof cached.cachedAt !== 'number'
        ) {
            return null
        }

        if (!allowStale && Date.now() - cached.cachedAt > BSV_EXCHANGE_RATE_CACHE_TTL_MS) {
            return null
        }

        return {
            exchangerate: cached.exchangerate,
            cachedAt: cached.cachedAt,
        }
    } catch {
        return null
    }
}

function writeCachedBsvExchangeRate(exchangeRate: number | null | undefined) {
    if (
        typeof window === 'undefined' ||
        typeof exchangeRate !== 'number' ||
        !Number.isFinite(exchangeRate) ||
        exchangeRate <= 0
    ) {
        return
    }

    try {
        window.localStorage.setItem(
            BSV_EXCHANGE_RATE_CACHE_KEY,
            JSON.stringify({
                exchangerate: exchangeRate,
                cachedAt: Date.now(),
            }),
        )
    } catch {}
}

// returns current bsv/usd exchange rate
export function useBsvExchangeRate({ enabled = true }: BsvExchangeRateOptions = {}) {
    const result = useQuery({
        queryKey: ['bsv-exchange-rate'],
        queryFn: async (): Promise<BsvExchangeRateResult> => {
            const response = await api.getBsvExchangeRate()
            if (typeof response?.exchangerate === 'number') {
                writeCachedBsvExchangeRate(response.exchangerate)
                return response
            }

            const staleCached = readCachedBsvExchangeRate({ allowStale: true })
            return staleCached ? { exchangerate: staleCached.exchangerate } : response
        },
        initialData: () => {
            const cached = readCachedBsvExchangeRate()
            return cached ? { exchangerate: cached.exchangerate } : undefined
        },
        initialDataUpdatedAt: () => readCachedBsvExchangeRate()?.cachedAt,
        staleTime: BSV_EXCHANGE_RATE_CACHE_TTL_MS,
        gcTime: Infinity,
        retry: 0,
        enabled,
        refetchOnMount: true, // refetch on mount only after the local cache is stale
        refetchOnWindowFocus: true, // refetch on focus only after the local cache is stale
        refetchOnReconnect: true, // refetch on reconnect only after the local cache is stale
    })

    // return null instead of 0 when loading or error
    return result.data?.exchangerate ?? null
}

// returns current BSV fee rate in sat/byte
export function useBsvFeeRate() {
    const result = useQuery({
        queryKey: ['bsv-fee-rate'],
        queryFn: api.getBsvFeeRate,
        staleTime: 30 * 1000,
        gcTime: Infinity,
        retry: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
    })

    return result.data?.fee_rate ?? 0.1
}

// converts sats to usd amount
export function useSatsToDollars(
    sats: number | null,
    { enabled = true }: BsvExchangeRateOptions = {},
) {
    const exchangeRate = useBsvExchangeRate({ enabled: enabled && sats != null && sats !== 0 })

    if (sats === 0) return 0
    if (sats == null || !exchangeRate) return null

    return satsToUsd(sats, exchangeRate)
}

// converts bsv to usd amount
export function useBsvToDollars(
    bsv: number | null,
    { enabled = true }: BsvExchangeRateOptions = {},
) {
    const exchangeRate = useBsvExchangeRate({ enabled: enabled && bsv != null && bsv !== 0 })

    if (bsv === 0) return 0
    if (bsv == null || !exchangeRate) return null

    return bsv * exchangeRate
}

// converts usd to sats amount
export function useDollarsToSats(dollars: number | null) {
    const exchangeRate = useBsvExchangeRate({ enabled: !!dollars })

    return usdToSats(dollars, exchangeRate)
}

// converts usd to bsv amount
export function useDollarsToBsv(dollars: number | null) {
    const exchangeRate = useBsvExchangeRate({ enabled: !!dollars })

    if (!dollars || !exchangeRate) return null

    return dollars / exchangeRate
}

// formats a usd amount into a display string
export function formatUsdValue(amount: number | null) {
    if (amount == null) return null
    if (amount === 0) return '$0'
    const absAmount = Math.abs(amount)
    if (absAmount < 0.001) return amount < 0 ? '> -$0.001' : '< $0.001'

    const formattedAmount = absAmount
        .toFixed(3)
        .replace(/(\.\d*?[1-9])0+$/, '$1')
        .replace(/\.0+$/, '')

    return `${amount < 0 ? '-' : ''}$${formattedAmount}`
}
